import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";

export const michiganExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {},
	delays: {},
}).createMachine({
	id: "MICHIGAN_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				{
					target: "INTRO",
					guard: ({ context, event }) => {
						const url = new URL(window.location.href);

						return (
							context.primaryResidenceValue === undefined &&
							url.searchParams.get("q") !== "primaryResidence"
						);
					},
				},
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) =>
						context.primaryResidenceValue === undefined,
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) => {
						return context.currentlyReceivingValue === undefined;
					},
				},
				{
					target: "PREVIOUS_ADDRESS_SEARCH",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingYesAtAnotherProperty(context) &&
						[
							context.previousAddressValue,
							context.previousCityValue,
							context.previousZipcodeValue,
						].some(value => value === undefined),
				},
				{
					target: "TRANSFER_EXEMPTION_REASON",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingYesAtAnotherProperty(context) &&
						context.specialFieldsValue?.exemption_transfer_reason === undefined,
				},
				{
					target: "MOVE_IN_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.moveInDateValue === undefined,
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.specialFieldsValue?.make_less_than_67k === true,
							context.specialFieldsValue?.is_65_years_or_older === true,
							context.specialFieldsValue?.disabled_veteran === true,
							context.specialFieldsValue?.surviving_spouse_of_a_veteran ===
								true,
							context.specialFieldsValue?.is_commercial_property === true,
							context.specialFieldsValue?.is_active_duty_personnel ===
								undefined,
							context.noneOfTheAbove === undefined,
						].some(value => value === true),
				},
				{
					target: "SSN_TIN",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.owner1HasEnteredSsnTin === undefined,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "ADDITIONAL_OWNER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						[
							context.owner2HasEnteredSsnTin === undefined,
							context.additionalOwnerFirstNameValue === undefined,
							context.additionalOwnerLastNameValue === undefined,
							context.additionalOwnerDobValue === undefined,
						].some(value => value === true),
				},
				{
					target: "OWNERSHIP_PERCENT",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						[
							context.ownersValue === "Other Persons",
							context.ownersValue === "Own With Spouse",
						].some(value => value === true) &&
						[
							context.ownerOneValue === undefined,
							context.ownerTwoValue === undefined,
						].some(value => value === true),
				},
				{
					target: "DL_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!context.ownerHasUploadedDlFile,
				},
				{
					target: "DL2_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						commonGuards.hasAdditionalOwners(context) &&
						!!context.ownerHasUploadedDlFile &&
						!context.owner2HasUploadedDlFile,
				},
				{
					target: "OUT_OF_STATE_TAX_RETURN",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.self_or_spouse_has_out_of_state_tax_return === undefined &&
						context.specialFieldsValue
							?.self_or_spouse_out_of_state_tax_return_state === undefined,
				},
				{
					target: "RESIDENCY_PROOF",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.specialFieldsValue
							?.self_or_spouse_has_out_of_state_tax_return === true &&
						context.specialFieldsValue
							?.self_or_spouse_out_of_state_tax_return_state !== undefined &&
						[
							(context.retroSavingsAmount ?? 0) > 0,
							[
								"Detroit",
								"Ann Arbor",
								"Grand Rapids",
								"Sterling Heights",
							].includes(context.city),
						].every(v => !!v),
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							context.currentlyReceivingValue === "no" ||
								context.currentlyReceivingValue === "notSure" ||
								context.currentlyReceivingValue === "yesAnotherProperty",
						].every(value => value === true),
				},
			],
		},
		INTRO: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				next: {
					target: "PRIMARY_RESIDENCE",
				},
			},
		},
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "INTRO",
					actions: assign({
						primaryResidenceValue: context => undefined,
					}),
				},
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (2 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
				next: [
					{
						target: "PREVIOUS_ADDRESS_SEARCH",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingYesAtAnotherProperty(context),
					},
					{
						target: "MOVE_IN_DATE",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
					},
				],
			},
		},
		PREVIOUS_ADDRESS_SEARCH: {
			entry: [
				assign({
					exemptionsProgress: context => (3 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "CURRENTLY_RECEIVING",
					actions: assign({
						previousAddressValue: context => undefined,
						previousAddress2Value: context => undefined,
						previousCityValue: context => undefined,
						previousStateValue: context => undefined,
						previousZipcodeValue: context => undefined,
						previousMoveOutDateValue: context => undefined,
					}),
				},
				next: {
					target: "TRANSFER_EXEMPTION_REASON",
				},
			},
		},
		TRANSFER_EXEMPTION_REASON: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "PREVIOUS_ADDRESS_SEARCH",
					actions: assign({
						specialFieldsValue: ({ context, event }) => ({
							...context.specialFieldsValue,
							exemption_transfer_reason: undefined,
						}),
					}),
				},
				next: [
					{
						target: "MOVE_IN_DATE",
					},
				],
			},
		},
		MOVE_IN_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						guard: ({ context, event }) =>
							!!context.specialFieldsValue?.exemption_transfer_reason,
						target: "TRANSFER_EXEMPTION_REASON",
						actions: assign({
							moveInDateValue: context => undefined,
						}),
					},
					{
						target: "CURRENTLY_RECEIVING",
						actions: assign({
							moveInDateValue: context => undefined,
						}),
					},
				],
				next: {
					target: "HOMESTEAD_QUALIFIED",
				},
			},
		},
		HOMESTEAD_QUALIFIED: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "MOVE_IN_DATE",
				},
				next: {
					target: "RARE_SITUATIONS",
				},
			},
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "HOMESTEAD_QUALIFIED",
						actions: assign({
							noneOfTheAbove: ({ context, event }) => undefined,
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								disabled_veteran: undefined,
								is_commercial_property: undefined,
								is_65_years_or_older: undefined,
								make_less_than_67k: undefined,
								surviving_spouse_of_a_veteran: undefined,
								is_active_duty_personnel: undefined,
							}),
						}),
					},
				],
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							[
								context.specialFieldsValue?.disabled_veteran === true,
								context.specialFieldsValue?.is_commercial_property === true,
								context.specialFieldsValue?.is_65_years_or_older === true,
								context.specialFieldsValue?.make_less_than_67k === true &&
									context.currentMarketValue &&
									context.currentMarketValue > 154_000,
								context.specialFieldsValue?.surviving_spouse_of_a_veteran ===
									true,
							].some(v => !!v),
					},
					{
						target: "SSN_TIN",
					},
				],
			},
		},
		SSN_TIN: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "RARE_SITUATIONS",
					actions: assign({
						owner1SsnTin: context => undefined,
					}),
				},
				next: {
					target: "OWNERS",
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (9 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "SSN_TIN",
						actions: assign({
							ownersValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							["Own With Spouse", "Other Persons"].includes(
								context.ownersValue ?? ""
							) ||
							(context.maritalStatusValue === "Married" &&
								context.ownersValue === "Sole Owner"),
					},
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		ADDITIONAL_OWNER: {
			entry: [
				assign({
					exemptionsProgress: context => (10 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "OWNERS",
					actions: assign({
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
						additionalOwnerDobValue: context => undefined,
						owner2SsnTin: context => undefined,
					}),
				},
				next: [
					{
						target: "OWNERSHIP_PERCENT",
					},
				],
			},
		},
		OWNERSHIP_PERCENT: {
			entry: [
				assign({
					exemptionsProgress: context => (11 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							context.maritalStatusValue !== "Married",
						actions: assign({
							ownerOneValue: context => undefined,
							ownerTwoValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		DL_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (12 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "OWNERS",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
					{
						target: "OWNERSHIP_PERCENT",
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
					{
						target: "OUT_OF_STATE_TAX_RETURN",
					},
				],
			},
		},
		DL2_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (13 / 17) * 100,
				}),
			],
			on: {
				back: {
					target: "DL_UPLOAD",
					actions: assign({
						owner2DlFilesToUpload: context => [],
						owner2DlExemptValue: context => undefined,
					}),
				},
				next: {
					target: "OUT_OF_STATE_TAX_RETURN",
				},
			},
		},
		OUT_OF_STATE_TAX_RETURN: {
			entry: [
				assign({
					exemptionsProgress: context => (14 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DL2_UPLOAD",
						guard: ({ context, event }) =>
							commonGuards.hasAdditionalOwners(context),
					},
					{
						target: "DL_UPLOAD",
					},
				],
				next: [
					{
						target: "RESIDENCY_PROOF",
						guard: ({ context, event }) =>
							[
								(context.retroSavingsAmount ?? 0) > 0,
								[
									"Detroit",
									"Ann Arbor",
									"Grand Rapids",
									"Sterling Heights",
								].includes(context.city),
							].every(v => !!v),
					},
					{
						target: "AGREEMENT",
					},
				],
			},
		},
		RESIDENCY_PROOF: {
			entry: [
				assign({
					exemptionsProgress: context => (15 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "OUT_OF_STATE_TAX_RETURN",
					},
				],
				next: {
					target: "AGREEMENT",
				},
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (16 / 17) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "RESIDENCY_PROOF",
						guard: ({ context, event }) =>
							[
								(context.retroSavingsAmount ?? 0) > 0,
								[
									"Detroit",
									"Ann Arbor",
									"Grand Rapids",
									"Sterling Heights",
								].includes(context.city),
							].every(v => !!v),
					},
					{
						target: "OUT_OF_STATE_TAX_RETURN",
					},
				],
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
